import React, { useState, useEffect, useMemo } from 'react';
import { apiKeyService, ApiKey } from '../../services/apiKeyService';
import { AdminService, AdminUser } from '../../services/adminService';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Timestamp } from 'firebase/firestore';
import {
    KeyIcon as FiKey,
    DocumentDuplicateIcon as FiCopy,
    TrashIcon as FiTrash2,
    MagnifyingGlassIcon as FiSearch,
    CheckCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';

interface AdminApiManagerProps {
}

const AdminApiManager: React.FC<AdminApiManagerProps> = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const { isDark } = useAdminTheme();
    const { showSuccess, showError } = useNotification();
    const [isLoading, setIsLoading] = useState(true);
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);
    const [showRevoked, setShowRevoked] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const [keys, allUsers] = await Promise.all([
                    apiKeyService.getAllApiKeys(),
                    AdminService.getAllUsers()
                ]);

                // Sort keys by createdAt in memory (descending)
                const sortedKeys = [...keys].sort((a, b) => {
                    const timeA = a.createdAt instanceof Timestamp ? a.createdAt.toDate().getTime() : (a.createdAt as any)?.seconds * 1000 || 0;
                    const timeB = b.createdAt instanceof Timestamp ? b.createdAt.toDate().getTime() : (b.createdAt as any)?.seconds * 1000 || 0;
                    return timeB - timeA;
                });

                setApiKeys(sortedKeys);
                setUsers(allUsers);
            } catch (error) {
                console.error('Error loading API keys:', error);
                showError('Failed to load API management data');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [showError]);

    const userMap = useMemo(() => {
        const map = new Map<string, AdminUser>();
        users.forEach(user => map.set(user.id, user));
        return map;
    }, [users]);

    const filteredKeys = useMemo(() => {
        return apiKeys.filter(key => {
            // Filter out revoked keys unless admin wants to see them
            if (!showRevoked && !key.isActive) return false;

            const user = userMap.get(key.userId);
            const userEmail = user?.email?.toLowerCase() || '';
            const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim().toLowerCase();
            const userDisplayName = user?.displayName?.toLowerCase() || '';
            const keyName = key.name?.toLowerCase() || '';
            const search = searchTerm.toLowerCase();

            return (
                !search ||
                userEmail.includes(search) ||
                userName.includes(search) ||
                userDisplayName.includes(search) ||
                keyName.includes(search) ||
                key.userId.toLowerCase().includes(search)
            );
        });
    }, [apiKeys, userMap, searchTerm, showRevoked]);

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
        showSuccess('API Key copied to clipboard');
    };

    const handleRevoke = async (id: string) => {
        try {
            // Soft-revoke: sets isActive=false, records revokedAt — preserves audit trail
            await apiKeyService.revokeApiKey(id);
            setApiKeys(prev => prev.map(k => k.id === id ? { ...k, isActive: false } : k));
            showSuccess('API Key revoked. The token is now inactive and will be rejected on next use.');
            setRevokeConfirmId(null);
        } catch (error) {
            console.error('Error revoking API key:', error);
            showError('Failed to revoke API key');
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
            </div>
        );
    }

    return (
        <div className={`p-6 space-y-6 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold">API Management</h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Monitor and manage all user-generated API access tokens
                    </p>
                </div>

                <div className="relative w-full md:w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiSearch className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                        type="text"
                        placeholder="Filter by user or key..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`block w-full pl-10 pr-3 py-2 border rounded-lg leading-5 focus:outline-none focus:ring-2 focus:ring-cyan-500 sm:text-sm transition-all duration-200 ${isDark
                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
                            }`}
                    />
                </div>
            </div>

            <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap justify-between items-center gap-3 bg-gray-50/50 dark:bg-gray-900/50">
                    <div className="flex items-center gap-2">
                        <FiKey className="w-5 h-5 text-cyan-500" />
                        <span className="font-semibold">
                            {filteredKeys.filter(k => k.isActive).length} Active
                            {showRevoked && ` · ${filteredKeys.filter(k => !k.isActive).length} Revoked`}
                        </span>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showRevoked}
                            onChange={e => setShowRevoked(e.target.checked)}
                            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>Show revoked</span>
                    </label>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className={`text-xs uppercase font-semibold ${isDark ? 'bg-gray-900/50 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                            <tr>
                                <th className="px-6 py-4">API Key Name</th>
                                <th className="px-6 py-4">User</th>
                                <th className="px-6 py-4">Workspace</th>
                                <th className="px-6 py-4">Token Preview</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Created</th>
                                <th className="px-6 py-4">Last Used</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            {filteredKeys.length > 0 ? (
                                filteredKeys.map((key) => {
                                    const user = userMap.get(key.userId);
                                    const isRevoked = !key.isActive;
                                    return (
                                        <tr key={key.id} className={`transition-colors ${isRevoked ? 'opacity-50' : ''} ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                                            <td className="px-6 py-4">
                                                <div className="font-medium">{key.name}</div>
                                                <div className="text-[10px] text-gray-500 font-mono mt-0.5">{key.id}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] ${isDark ? 'bg-cyan-900/30 text-cyan-400' : 'bg-cyan-100 text-cyan-700'}`}>
                                                        {user?.email?.[0].toUpperCase() || 'U'}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Unknown User'}</div>
                                                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email || 'No email'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={`px-6 py-4 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {key.workspaceId
                                                    ? <span className="font-mono">{key.workspaceId}</span>
                                                    : <span className="italic text-gray-400">All workspaces</span>
                                                }
                                            </td>
                                            <td className="px-6 py-4">
                                                <code className={`rounded px-2 py-1 text-xs font-mono ${isDark ? 'bg-gray-900 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                                                    {key.keyPreview || 'Token hidden'}
                                                </code>
                                            </td>
                                            <td className="px-6 py-4">
                                                {isRevoked ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                                                        <XCircleIcon className="w-3.5 h-3.5" />
                                                        Revoked
                                                        {key.revokedAt && <span className="text-[10px] text-gray-400 ml-1">{formatDate(key.revokedAt)}</span>}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                                                        <CheckCircleIcon className="w-3.5 h-3.5" />
                                                        Active
                                                    </span>
                                                )}
                                            </td>
                                            <td className={`px-6 py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {formatDate(key.createdAt)}
                                            </td>
                                            <td className={`px-6 py-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {key.lastUsedAt ? formatDate(key.lastUsedAt) : <span className="italic text-gray-400">Never</span>}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {!isRevoked && (
                                                    <button
                                                        onClick={() => setRevokeConfirmId(key.id)}
                                                        className="p-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                                                        title="Revoke token"
                                                    >
                                                        <FiTrash2 className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 italic">
                                        {searchTerm ? 'No API keys match your search criteria' : 'No API keys found in the system'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Revoke Confirmation Modal */}
            {revokeConfirmId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className={`w-full max-w-md rounded-2xl p-6 shadow-2xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
                                <FiTrash2 className="w-8 h-8" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">Revoke API Key?</h3>
                                <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Are you sure you want to revoke this API key? This action is permanent and will immediately terminate all access for applications using this token.
                                </p>
                            </div>
                            <div className="flex w-full gap-3 mt-4">
                                <button
                                    onClick={() => setRevokeConfirmId(null)}
                                    className={`flex-1 px-4 py-2 rounded-xl font-semibold transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                                        }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleRevoke(revokeConfirmId)}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors"
                                >
                                    Revoke Key
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminApiManager;
